import { CommonModule } from '@angular/common';
import {
	afterNextRender,
	Component,
	computed,
	inject,
	Injector,
	input,
	Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Params } from '@angular/router';
import { Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import type { InputsType } from 'ng-dynamic-component';
import { DynamicModule } from 'ng-dynamic-component';
import type { Observable } from 'rxjs';
import { combineLatest, from, map, switchMap } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { AlertType } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { SkeletonComponent } from '@valk-nx/components/ui-skeleton/src/lib/skeleton';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import type { Language } from '@valk-nx/core/lib/core';
import { fallbackLanguage, HOTEL_SLUG, LANGUAGE } from '@valk-nx/core/lib/core';
import { SectionBgClass } from '@valk-nx/core/lib/pipes/section-class/section-class.pipe';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { CarouselWithAvailabilityWidgetComponent } from '@valk-nx/storyblok/components/carousel-with-availability-widget/src/lib/carousel-with-availability-widget';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { buildIconListRoom } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import {
	removeParameters,
	removeTrailingSlash,
} from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import type { RoomCategory } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import type { RoomsByCategory } from './room-overview.interface';

const Components = {
	carousel: SbCarouselComponent,
	carouselWithAvailabilityWidget: CarouselWithAvailabilityWidgetComponent,
	section: SectionComponent,
};

@Component({
	selector: 'sb-room-overview',
	templateUrl: './room-overview.component.html',
	imports: [
		AlertComponent,
		CommonModule,
		DynamicModule,
		PersuadeCardComponent,
		SectionBgClass,
		SkeletonComponent,
		TranslatePipe,
	],
})
export class RoomOverviewComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly renderer2 = inject(Renderer2);
	private readonly router = inject(Router);
	private readonly injector = inject(Injector);
	readonly hotelSlug = inject(HOTEL_SLUG);
	readonly bookingtool = inject(BOOKINGTOOL);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	content = input.required<ISbStoryBlok[]>();

	components: typeof Components = Components;
	currentUrl = '';
	roomsData$: Observable<RoomsByCategory[]>;
	alertType: AlertType = 'dialog--warning';
	slugLanguage: string = fallbackLanguage;

	contentInputs = computed<(InputsType & { component: string })[]>(() => {
		return this.content().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		}));
	});

	constructor() {
		super();
		this.slugLanguage = this.rootLanguage;
		this.roomsData$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				const { url } = this.router;
				this.currentUrl = removeTrailingSlash(removeParameters(url));

				const selectedSlug =
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0];
				this.slugLanguage = selectedSlug.lang.toLowerCase();
				const path =
					this.slugLanguage === this.rootLanguage
						? selectedSlug.path
						: selectedSlug.path.substring(3);
				return { path, language: this.slugLanguage as Language };
			}),
			switchMap(({ path, language }) =>
				combineLatest([
					from(
						this.sbService.getStoriesByQuery(
							{
								starts_with: path,
								excluding_slugs: path,
								per_page: 100,
							},
							language,
						),
					),
					this.contentService.getRooms(language),
				]),
			),
			map(([storiesResponse, roomsResponse]) => {
				const storyblokRooms = new Set(
					storiesResponse.data.stories?.map((rooms) =>
						rooms.full_slug.split('/').pop(),
					),
				);
				const categoriesByName = new Map<string, RoomCategory>();
				roomsResponse.data.forEach((room) => {
					if (!categoriesByName.has(room.category.name)) {
						categoriesByName.set(room.category.name, room.category);
					}
				});

				const rooms = roomsResponse?.data
					.filter((room) => storyblokRooms.has(room.slug))
					.map((room) => {
						let bookingUrl: string;
						let bookingToolQueryParams: Params = {};
						if (this.bookingtool) {
							bookingUrl =
								BookingRedirectHelper.getBookingToolUrl(
									this.bookingtool,
									this.hotelSlug,
									{
										roomGuid: room.guid,
										languageCode: this.slugLanguage,
									},
								);
						} else {
							bookingUrl =
								this.slugLanguage === this.rootLanguage
									? '/booking'
									: `/${this.slugLanguage}/booking`;
							bookingToolQueryParams =
								BookingRedirectHelper.toQueryParamRecord({
									roomGuid: room.guid,
								});
						}
						return {
							...room,
							pricingToolTip: PriceHelper.getPricingTooltip(
								room.occupancyRestrictions,
							),
							bookingToolRedirectUrl: bookingUrl,
							bookingToolQueryParams: bookingToolQueryParams,
							iconList: buildIconListRoom(room),
						};
					});

				this.metadataService.setSchemaOrgRoomList(
					this.renderer2,
					this.metaTitle(),
					rooms,
				);

				const roomsByCategory = [...categoriesByName.values()].map(
					(category) => ({
						category,
						rooms: rooms.filter(
							(room) => room.category.name === category.name,
						),
					}),
				);

				this.scrollToCategoryInFragment(roomsByCategory);

				return roomsByCategory;
			}),
		);
	}

	private scrollToCategoryInFragment(
		roomsByCategory: RoomsByCategory[],
	): void {
		const { fragment } = this.router.parseUrl(this.router.url);
		if (!fragment) {
			return;
		}

		const isRenderedCategory = roomsByCategory.some(
			({ category, rooms }) =>
				category.type === fragment && rooms.length > 0,
		);
		if (!isRenderedCategory) {
			return;
		}

		afterNextRender(
			() => GeneralHelper.scrollToElementWithId(fragment, 0, false),
			{ injector: this.injector },
		);
	}
}
